import { useState } from 'react';

import { useSaveWeeklySchedule, useWeeklySchedule } from '@/hooks/scheduling';
import {
  SCHEDULE_PRESETS,
  workingMinutesFor,
  type WeeklyScheduleDayView,
} from '@shared/schemas/availability';

/*
 * The editing state behind the weekly template — ROADMAP.md §7.2.
 *
 * **It stores the edits, not the week.** The rendered week is the server's seven
 * rows with whatever has been changed laid over the top, computed on every
 * render. That is deliberate rather than incidental: the obvious version copies
 * the server's week into state when it arrives, which needs an effect, and a
 * `setState` inside an effect is a cascading render *and* a stale draft the
 * first time a save's response comes back looking different from what was sent.
 * With edits-over-server there is nothing to synchronise — clearing the edits is
 * the whole of "reset to what was saved".
 *
 * It is a hook rather than component state because the running weekly total sits
 * in the pane's header while the rows are in the editor below it, and both have
 * to read the same unsaved figure. One source, two consumers.
 */

/** What the form holds. `HH:MM`, because that is what `<input type="time">` speaks. */
export interface DayDraft {
  dayOfWeek: number;
  dayLabel: string;
  isWorking: boolean;
  startTime: string;
  endTime: string;
  breakMinutes: number;
}

/** Filled in the moment a day is switched on, so a working day is never blank. */
const DEFAULT_START = '08:00';
const DEFAULT_END = '17:00';

function toDraft(day: WeeklyScheduleDayView): DayDraft {
  return {
    dayOfWeek: day.dayOfWeek,
    dayLabel: day.dayLabel,
    isWorking: day.isWorking,
    // Trimmed to `HH:MM` — the server hands back `HH:MM:SS`, and a time input
    // silently refuses a value it cannot parse, which renders as an empty box
    // nobody can explain.
    startTime: day.startTime?.slice(0, 5) ?? DEFAULT_START,
    endTime: day.endTime?.slice(0, 5) ?? DEFAULT_END,
    breakMinutes: day.breakMinutes,
  };
}

/**
 * Hours a draft day is worth, through the **same function the server uses**.
 * A row badge reading `8h 30m` beside a total computed another way is the kind
 * of disagreement nobody can debug from a screenshot.
 */
export function minutesOf(draft: DayDraft): number {
  return workingMinutesFor({
    isWorking: draft.isWorking,
    startTime: draft.startTime,
    endTime: draft.endTime,
    breakMinutes: draft.breakMinutes,
  });
}

/** A day claiming to work with a finish at or before its start. */
export function isInvalid(draft: DayDraft): boolean {
  return draft.isWorking && draft.endTime <= draft.startTime;
}

export interface WeeklyDraft {
  query: ReturnType<typeof useWeeklySchedule>;
  save: ReturnType<typeof useSaveWeeklySchedule>;
  /** The server's week with the unsaved edits laid over it. Empty until loaded. */
  days: DayDraft[];
  totals: { daysWorking: number; minutes: number };
  dirty: boolean;
  /** True while any working day has its finish at or before its start. */
  hasInvalidDay: boolean;
  update: (dayOfWeek: number, changes: Partial<DayDraft>) => void;
  applyPreset: (presetId: string) => void;
  discard: () => void;
  submit: (handlers: { onSuccess: () => void; onError: (error: unknown) => void }) => void;
}

export function useWeeklyDraft(employeeId: string): WeeklyDraft {
  const query = useWeeklySchedule(employeeId);
  const save = useSaveWeeklySchedule(employeeId);

  // Keyed by day of week. An absent key means "as the server has it".
  const [edits, setEdits] = useState<Record<number, DayDraft>>({});

  const saved = (query.data?.days ?? []).map(toDraft);
  const days = saved.map((day) => edits[day.dayOfWeek] ?? day);

  const totals = {
    daysWorking: days.filter((day) => day.isWorking).length,
    minutes: days.reduce((total, day) => total + minutesOf(day), 0),
  };

  return {
    query,
    save,
    days,
    totals,
    dirty: JSON.stringify(saved) !== JSON.stringify(days),
    hasInvalidDay: days.some(isInvalid),

    update(dayOfWeek, changes) {
      setEdits((current) => {
        const base = current[dayOfWeek] ?? saved.find((day) => day.dayOfWeek === dayOfWeek);
        if (!base) return current;
        return { ...current, [dayOfWeek]: { ...base, ...changes } };
      });
    },

    applyPreset(presetId) {
      const preset = SCHEDULE_PRESETS.find((entry) => entry.id === presetId);
      if (!preset) return;

      setEdits(
        Object.fromEntries(
          saved.map((day) => [
            day.dayOfWeek,
            preset.days.includes(day.dayOfWeek)
              ? {
                  ...day,
                  isWorking: true,
                  startTime: preset.startTime.slice(0, 5),
                  endTime: preset.endTime.slice(0, 5),
                  breakMinutes: preset.breakMinutes,
                }
              : // A preset sets the **whole** week, including the days it leaves
                // out. Applying "Part-time M/W/F" over "Mon–Sat" has to turn
                // Saturday off, or the two compose into a week nobody chose.
                { ...day, isWorking: false, breakMinutes: 0 },
          ]),
        ),
      );
    },

    discard() {
      setEdits({});
    },

    submit({ onSuccess, onError }) {
      save.mutate(
        {
          days: days.map((day) => ({
            dayOfWeek: day.dayOfWeek,
            isWorking: day.isWorking,
            // Cleared on a day off rather than kept "in case". A row that is not
            // working with hours still on it reads as an answer the next time
            // somebody widens the resolver, and it would be the wrong one.
            startTime: day.isWorking ? day.startTime : null,
            endTime: day.isWorking ? day.endTime : null,
            breakMinutes: day.isWorking ? day.breakMinutes : 0,
          })),
        },
        {
          onSuccess: () => {
            // The edits *are* what was saved now, so dropping them makes the
            // server's response the new baseline without a comparison.
            setEdits({});
            onSuccess();
          },
          onError,
        },
      );
    },
  };
}
